import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, PackagePlus, Plus, RefreshCw, Save, Trash2, TrendingDown, TrendingUp, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { IngredientPicker } from "./IngredientPicker";
import { performStockAction } from "@/lib/stockSync";
import { EventType } from "@/lib/events";
import { R, fmtQty, pct, rangeToDates, RangePicker, ExplainerBox, EmptyState, ErrorState, errMsg, fmtWhen, isoDate, useStockLocations } from "./shared";

const UNITS = ["kg", "g", "L", "ml", "pcs", "tots"];
const newLine = (patch = {}) => ({ key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ingredient_id: "", quantity: "", unit: "", total_cost: "", ...patch });
const EMPTY_HEADER = { supplier: "", invoice_number: "", invoice_date: isoDate(new Date()), location: "back_of_house", update_stock: true, update_cost: true, notes: "" };
const newDeliveryId = () => `dlv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Session 47 — Inventory → Receiving (batch).
 * ONE invoice header (supplier / invoice # / date / location) and as many
 * lines as the delivery has. Every line is posted with the same
 * `delivery_id` so the whole invoice groups together. "Update stock" can
 * be switched off to record the invoice (cost history, delivery log)
 * without touching stock — the manager then counts and updates by hand.
 */
export const ReceivingPanel = ({ http, authHeaders, ingredients = [], sessionUser, onStockChanged, sessionForEvents }) => {
  const [header, setHeader] = useState(EMPTY_HEADER);
  const [costMode, setCostMode] = useState("total"); // "total" (line total) | "unit"
  const [lines, setLines] = useState([newLine(), newLine(), newLine()]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastDelivery, setLastDelivery] = useState(null);
  const stockLocs = useStockLocations(http, authHeaders);
  const locationOptions = useMemo(() => (stockLocs.active.length ? stockLocs.active.map((l) => ({ value: l.key || l.id, label: l.name })) : [{ value: "back_of_house", label: "Back of house" }, { value: "floor", label: "Floor / Kitchen" }]), [stockLocs.active]);

  // Presets
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("");
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");

  // Right-hand side: recent deliveries + price trend
  const [deliveries, setDeliveries] = useState(null);
  const [trend, setTrend] = useState(null);
  const [changes, setChanges] = useState(null);
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sideTab, setSideTab] = useState("deliveries");

  const ingById = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  const load = useCallback(async (rangeKey) => {
    setLoading(true);
    setError("");
    try {
      const [d, t, c, p] = await Promise.all([
        http.get("/owner/inventory/deliveries", { headers: authHeaders, params: { limit: 25 } }),
        http.get("/owner/inventory/price-trend", { headers: authHeaders }),
        http.get("/owner/inventory/price-changes", { headers: authHeaders, params: rangeToDates(rangeKey || range) }),
        http.get("/owner/inventory/receiving-presets", { headers: authHeaders }),
      ]);
      setDeliveries(d.data?.deliveries || []);
      setTrend(t.data || null);
      setChanges(c.data?.changes || []);
      setPresets(p.data?.presets || []);
    } catch (e) {
      setError(errMsg(e, "Unable to load receiving history."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders, range]);

  // Mount-only load (pickers/range changes call load() themselves).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(range); }, []);

  // ── line helpers ─────────────────────────────────────────────────────
  const updateLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : [newLine()]));
  const addLine = () => setLines((ls) => [...ls, newLine()]);

  const lineCalc = (l) => {
    const ing = ingById[l.ingredient_id];
    const qty = Number(l.quantity);
    const amt = Number(l.total_cost);
    if (!ing || !Number.isFinite(qty) || qty <= 0 || l.total_cost === "" || !Number.isFinite(amt)) return { ing, qty: Number.isFinite(qty) ? qty : 0, total: null, perUnit: null, changePct: null };
    const total = costMode === "total" ? amt : amt * qty;
    const unit = l.unit || ing.unit;
    const perUnit = unit === ing.unit ? total / qty : null;
    const prev = Number(ing.cost_per_unit);
    const changePct = perUnit !== null && Number.isFinite(prev) && prev > 0 ? ((perUnit - prev) / prev) * 100 : null;
    return { ing, qty, total, perUnit, prev: Number.isFinite(prev) ? prev : null, changePct };
  };

  const filled = lines.filter((l) => l.ingredient_id && Number(l.quantity) > 0);
  const invoiceTotal = filled.reduce((s, l) => s + (lineCalc(l).total || 0), 0);

  const applyPreset = (id) => {
    setPresetId(id);
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setLines(p.lines.filter((l) => !l.missing).map((l) => {
      const cost = l.last_unit_cost ?? l.unit_cost ?? l.current_cost_per_unit;
      const qty = l.quantity ?? "";
      return newLine({
        ingredient_id: l.ingredient_id,
        quantity: qty === null ? "" : String(qty),
        unit: l.unit || l.stock_unit || "",
        total_cost: cost !== null && cost !== undefined && qty ? (costMode === "total" ? String(Math.round(cost * Number(qty) * 100) / 100) : String(cost)) : "",
      });
    }).concat([newLine()]));
    if (p.supplier) setHeader((h) => ({ ...h, supplier: p.supplier }));
    toast.info(`Loaded "${p.name}" — ${p.lines.length} line(s). Adjust quantities to match the invoice.`);
  };

  const savePreset = async () => {
    if (!presetName.trim()) { toast.error("Give the preset a name."); return; }
    const body = {
      name: presetName.trim(),
      supplier: header.supplier.trim() || null,
      lines: filled.map((l) => {
        const c = lineCalc(l);
        return { ingredient_id: l.ingredient_id, quantity: c.qty || null, unit: l.unit || null, unit_cost: c.perUnit !== null ? Math.round(c.perUnit * 10000) / 10000 : null };
      }),
    };
    if (!body.lines.length) { toast.error("Add at least one line first."); return; }
    try {
      const r = await http.post("/owner/inventory/receiving-presets", body, { headers: authHeaders });
      setPresets((ps) => [...ps, r.data?.preset].filter(Boolean));
      setPresetId(r.data?.preset?.id || "");
      setSavePresetOpen(false);
      setPresetName("");
      toast.success(`Preset "${body.name}" saved.`);
    } catch (e) {
      toast.error(errMsg(e, "Unable to save preset."));
    }
  };

  const deletePreset = async () => {
    if (!presetId) return;
    const p = presets.find((x) => x.id === presetId);
    if (!window.confirm(`Delete preset "${p?.name}"?`)) return;
    try {
      await http.delete(`/owner/inventory/receiving-presets/${presetId}`, { headers: authHeaders });
      setPresets((ps) => ps.filter((x) => x.id !== presetId));
      setPresetId("");
    } catch (e) {
      toast.error(errMsg(e, "Unable to delete preset."));
    }
  };

  // ── submit the whole invoice ─────────────────────────────────────────
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!filled.length) { toast.error("Add at least one line with an ingredient and quantity."); return; }
    const bad = filled.find((l) => l.total_cost !== "" && !(Number(l.total_cost) >= 0));
    if (bad) { toast.error("Costs must be zero or more."); return; }
    const deliveryId = newDeliveryId();
    setSaving(true);
    setProgress({ done: 0, total: filled.length });
    const results = [];
    let queued = 0;
    let failed = 0;
    for (const l of filled) {
      const c = lineCalc(l);
      const body = {
        ingredient_id: l.ingredient_id,
        quantity: c.qty,
        unit: l.unit || null,
        supplier: header.supplier.trim() || null,
        invoice_number: header.invoice_number.trim() || null,
        invoice_date: header.invoice_date || null,
        location: header.location,
        update_cost: !!header.update_cost,
        update_stock: !!header.update_stock,
        notes: header.notes.trim() || null,
        delivery_id: deliveryId,
        preset_id: presetId || null,
      };
      if (l.total_cost !== "") {
        if (costMode === "total") body.total_cost = Number(l.total_cost); else body.unit_cost = Number(l.total_cost);
      }
      try {
        const res = await performStockAction({ http, authHeaders, path: "/owner/inventory/receive", body, eventType: EventType.STOCK_RECEIVED, session: sessionForEvents || null });
        if (res.status === "queued") queued += 1; else results.push({ ...res.data, ingredient_name: c.ing?.name });
      } catch (err) {
        failed += 1;
        toast.error(`${c.ing?.name || "Line"}: ${errMsg(err, "not received")}`);
      }
      setProgress((p) => ({ ...p, done: (p?.done || 0) + 1 }));
    }
    setSaving(false);
    setProgress(null);
    const okCount = results.length + queued;
    if (okCount) {
      const moved = results.filter((r) => r.price_change && Math.abs(Number(r.price_change.change_pct) || 0) >= 0.5);
      toast.success(`${header.update_stock ? "Received" : "Recorded"} ${okCount} line${okCount === 1 ? "" : "s"}${header.invoice_number ? ` on invoice #${header.invoice_number}` : ""}${invoiceTotal ? ` · ${R(invoiceTotal)}` : ""}${!header.update_stock ? " — stock left unchanged" : ""}.${queued ? ` ${queued} queued for sync.` : ""}`, moved.length ? { description: `${moved.length} ingredient cost${moved.length === 1 ? "" : "s"} changed — see the summary.` } : undefined);
      setLastDelivery({ header: { ...header }, results, queued, failed, total: invoiceTotal });
      setLines([newLine(), newLine(), newLine()]);
      setHeader((h) => ({ ...EMPTY_HEADER, location: h.location, update_stock: h.update_stock, update_cost: h.update_cost }));
      setPresetId("");
      onStockChanged?.();
      load(range);
    }
  };

  const affectedAll = useMemo(() => (lastDelivery?.results || []).flatMap((r) => (r.affected_menu_items || []).map((m) => ({ ...m, via: r.ingredient_name }))), [lastDelivery]);

  return (
    <div className="space-y-3" data-testid="receiving-panel">
      <ExplainerBox title="What is this tab?" testId="receiving-explainer">
        <p>Book a whole delivery in one go: fill in the invoice header once, then list every item on it. All lines are saved under the same invoice. Leave <b>Update stock</b> on to add the quantities to stock, or switch it off to record the invoice only and count/update stock yourself afterwards.</p>
      </ExplainerBox>

      <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        {/* ── Invoice entry ─────────────────────────────────────────── */}
        <Card className="min-w-0 rounded-2xl border-border bg-card" data-testid="receiving-form-card">
          <CardContent className="p-4">
            <form className="space-y-3" onSubmit={submit}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold">Receive a delivery</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Recorded by <span className="font-medium text-foreground">{sessionUser?.full_name || "Current user"}</span>.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={presetId || "none"} onValueChange={(v) => (v === "none" ? setPresetId("") : applyPreset(v))}>
                    <SelectTrigger className="h-9 w-52 rounded-xl text-xs" data-testid="receive-preset-select"><SelectValue placeholder="Load a preset…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No preset</SelectItem>
                      {presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.supplier ? ` · ${p.supplier}` : ""} ({p.line_count})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" className="h-9 rounded-xl" onClick={() => { setPresetName(presets.find((p) => p.id === presetId)?.name || header.supplier || ""); setSavePresetOpen(true); }} disabled={!filled.length} data-testid="receive-save-preset">
                    <Save className="mr-1 h-3.5 w-3.5" /> Save as preset
                  </Button>
                  {presetId ? <Button type="button" size="sm" variant="ghost" className="h-9 rounded-xl text-destructive" onClick={deletePreset} data-testid="receive-delete-preset"><Trash2 className="h-3.5 w-3.5" /></Button> : null}
                </div>
              </div>

              {/* Header */}
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="receive-supplier">Supplier</Label>
                  <Input id="receive-supplier" value={header.supplier} onChange={(e) => setHeader((h) => ({ ...h, supplier: e.target.value }))} className="h-10 rounded-xl" placeholder="e.g. Cape Meat Co" data-testid="receive-supplier" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="receive-invoice">Invoice #</Label>
                  <Input id="receive-invoice" value={header.invoice_number} onChange={(e) => setHeader((h) => ({ ...h, invoice_number: e.target.value }))} className="h-10 rounded-xl" placeholder="INV-1042" data-testid="receive-invoice" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="receive-invoice-date">Invoice date</Label>
                  <Input id="receive-invoice-date" type="date" value={header.invoice_date} max={isoDate(new Date())} onChange={(e) => setHeader((h) => ({ ...h, invoice_date: e.target.value }))} className="h-10 rounded-xl" data-testid="receive-invoice-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Put into</Label>
                  <Select value={header.location} onValueChange={(v) => setHeader((h) => ({ ...h, location: v }))}>
                    <SelectTrigger className="h-10 rounded-xl" data-testid="receive-location"><SelectValue /></SelectTrigger>
                    <SelectContent>{locationOptions.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`receive-location-option-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Lines */}
              <div className="rounded-xl border border-border">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
                  <p className="text-xs font-medium">Items on the invoice <span className="text-muted-foreground">({filled.length})</span></p>
                  <Tabs value={costMode} onValueChange={setCostMode}>
                    <TabsList className="h-7 rounded-lg">
                      <TabsTrigger value="total" className="rounded-md px-2 text-[11px]" data-testid="receive-cost-mode-total">Cost = line total</TabsTrigger>
                      <TabsTrigger value="unit" className="rounded-md px-2 text-[11px]" data-testid="receive-cost-mode-unit">Cost = per unit</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <div className="hidden grid-cols-[minmax(0,1fr)_96px_84px_120px_150px_36px] gap-2 px-3 pt-2 text-[11px] text-muted-foreground md:grid">
                  <span>Ingredient</span><span>Qty</span><span>Unit</span><span>{costMode === "total" ? "Line total (R)" : "Cost / unit (R)"}</span><span>Landed cost vs current</span><span />
                </div>
                <div className="space-y-2 p-3" data-testid="receive-lines">
                  {lines.map((l, idx) => {
                    const c = lineCalc(l);
                    return (
                      <div key={l.key} className="grid grid-cols-2 gap-2 rounded-lg border border-border/60 p-2 md:grid-cols-[minmax(0,1fr)_96px_84px_120px_150px_36px] md:items-center md:border-0 md:p-0" data-testid={`receive-line-${idx}`}>
                        <div className="col-span-2 md:col-span-1">
                          <IngredientPicker ingredients={ingredients} value={l.ingredient_id} onChange={(id, ing) => updateLine(l.key, { ingredient_id: id, unit: ing?.unit || "" })} testId={`receive-line-${idx}-ingredient`} />
                        </div>
                        <Input type="number" inputMode="decimal" min="0" step="any" value={l.quantity} onChange={(e) => updateLine(l.key, { quantity: e.target.value })} className="h-10 rounded-xl tabular-nums" placeholder="Qty" data-testid={`receive-line-${idx}-qty`} />
                        <Select value={l.unit || c.ing?.unit || "pcs"} onValueChange={(v) => updateLine(l.key, { unit: v })}>
                          <SelectTrigger className="h-10 rounded-xl" data-testid={`receive-line-${idx}-unit`}><SelectValue /></SelectTrigger>
                          <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" inputMode="decimal" min="0" step="0.01" value={l.total_cost} onChange={(e) => updateLine(l.key, { total_cost: e.target.value })} className="h-10 rounded-xl tabular-nums" placeholder={costMode === "total" ? "R total" : "R / unit"} data-testid={`receive-line-${idx}-cost`} />
                        <div className="text-[11px] leading-tight text-muted-foreground" data-testid={`receive-line-${idx}-preview`}>
                          {c.perUnit !== null ? (
                            <>
                              <span className="font-medium text-foreground">{R(c.perUnit)}/{c.ing.unit}</span>
                              {c.changePct !== null ? (
                                <Badge variant="outline" className={`ml-1 rounded-md px-1 text-[10px] ${c.changePct > 0.5 ? "border-destructive/40 text-destructive" : c.changePct < -0.5 ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400" : ""}`}>
                                  {c.changePct > 0.5 ? <TrendingUp className="mr-0.5 inline h-3 w-3" /> : c.changePct < -0.5 ? <TrendingDown className="mr-0.5 inline h-3 w-3" /> : null}
                                  {c.changePct > 0 ? "+" : ""}{c.changePct.toFixed(1)}% vs {R(c.prev)}
                                </Badge>
                              ) : c.prev === null ? <span className="ml-1">first cost</span> : null}
                            </>
                          ) : c.ing && c.total !== null ? <span>server converts to {c.ing.unit}</span> : c.ing ? <span>now {Number.isFinite(Number(c.ing.cost_per_unit)) ? `${R(c.ing.cost_per_unit)}/${c.ing.unit}` : "no cost"} · on hand {fmtQty(c.ing.stock_on_hand)}</span> : null}
                        </div>
                        <Button type="button" size="icon" variant="ghost" className="h-9 w-9 justify-self-end rounded-lg text-muted-foreground hover:text-destructive md:justify-self-auto" onClick={() => removeLine(l.key)} aria-label="Remove line" data-testid={`receive-line-${idx}-remove`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    );
                  })}
                  <Button type="button" variant="outline" size="sm" className="h-9 rounded-xl" onClick={addLine} data-testid="receive-add-line"><Plus className="mr-1 h-4 w-4" /> Add item</Button>
                </div>
                <div className="flex items-center justify-between border-t border-border px-3 py-2 text-sm">
                  <span className="text-xs text-muted-foreground">{filled.length} item{filled.length === 1 ? "" : "s"}</span>
                  <span className="font-semibold tabular-nums" data-testid="receive-invoice-total">Invoice total {R(invoiceTotal)}</span>
                </div>
              </div>

              {/* Options */}
              <div className="grid gap-2 sm:grid-cols-2">
                <div className={`flex items-center justify-between rounded-xl border px-3 py-2 ${header.update_stock ? "border-border bg-secondary/40" : "border-amber-500/40 bg-amber-500/10"}`}>
                  <div>
                    <p className="text-xs font-medium">Update stock</p>
                    <p className="text-[11px] text-muted-foreground">{header.update_stock ? "Quantities are added to the chosen location." : "Record the invoice only — stock stays as is, update it by hand after counting."}</p>
                  </div>
                  <Switch checked={header.update_stock} onCheckedChange={(v) => setHeader((h) => ({ ...h, update_stock: v }))} data-testid="receive-update-stock" />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3 py-2">
                  <div>
                    <p className="text-xs font-medium">Update ingredient cost</p>
                    <p className="text-[11px] text-muted-foreground">Recipes and margins use the new landed cost from now on.</p>
                  </div>
                  <Switch checked={header.update_cost} onCheckedChange={(v) => setHeader((h) => ({ ...h, update_cost: v }))} data-testid="receive-update-cost" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs" htmlFor="receive-notes">Notes (optional)</Label>
                <Textarea id="receive-notes" value={header.notes} onChange={(e) => setHeader((h) => ({ ...h, notes: e.target.value }))} rows={2} className="rounded-xl" data-testid="receive-notes" />
              </div>
              <Button type="submit" className="h-11 w-full rounded-xl" disabled={saving || !filled.length} data-testid="receive-submit">
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <PackagePlus className="mr-1 h-4 w-4" />}
                {saving && progress ? `Saving ${progress.done}/${progress.total}…` : header.update_stock ? `Receive ${filled.length || ""} item${filled.length === 1 ? "" : "s"} into stock` : `Record invoice (${filled.length} item${filled.length === 1 ? "" : "s"}) without updating stock`}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* ── Right column ───────────────────────────────────────────── */}
        <div className="min-w-0 space-y-3">
          {lastDelivery ? (
            <Card className="rounded-2xl border-emerald-500/40 bg-emerald-500/10" data-testid="receive-last-result">
              <CardContent className="p-4">
                <p className="text-sm font-semibold">Last delivery{lastDelivery.header.invoice_number ? ` · #${lastDelivery.header.invoice_number}` : ""}{lastDelivery.header.supplier ? ` · ${lastDelivery.header.supplier}` : ""}</p>
                <p className="mt-1 text-xs text-muted-foreground">{lastDelivery.results.length + lastDelivery.queued} line(s) · {R(lastDelivery.total)}{lastDelivery.header.update_stock ? "" : " · stock NOT updated"}{lastDelivery.queued ? ` · ${lastDelivery.queued} queued` : ""}{lastDelivery.failed ? ` · ${lastDelivery.failed} failed` : ""}</p>
                <ul className="mt-2 space-y-0.5 text-xs">
                  {lastDelivery.results.map((r, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span>{r.ingredient_name} · +{fmtQty(r.received?.quantity)} {r.received?.unit}</span>
                      <span className={`tabular-nums ${r.price_change && Number(r.price_change.change_pct) > 0.5 ? "text-destructive" : r.price_change && Number(r.price_change.change_pct) < -0.5 ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}>
                        {r.price_change ? `${R(r.price_change.previous)} → ${R(r.price_change.new)} (${Number(r.price_change.change_pct) > 0 ? "+" : ""}${r.price_change.change_pct ?? "new"}%)` : "cost unchanged"}
                      </span>
                    </li>
                  ))}
                </ul>
                {affectedAll.length ? (
                  <div className="mt-3 overflow-x-auto">
                    <p className="mb-1 text-xs font-medium">Menu items whose margin just moved</p>
                    <Table>
                      <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Cost before → after</TableHead><TableHead className="text-right">Margin</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {affectedAll.slice(0, 12).map((m, i) => {
                          const worse = (m.margin_after_pct ?? 0) < (m.margin_before_pct ?? 0);
                          return (
                            <TableRow key={`${m.id}-${i}`}>
                              <TableCell className="text-xs font-medium">{m.name}</TableCell>
                              <TableCell className="text-right text-xs tabular-nums">{R(m.cost_before)} → {R(m.cost_after)}</TableCell>
                              <TableCell className={`text-right text-xs font-semibold tabular-nums ${worse ? "text-destructive" : "text-emerald-700 dark:text-emerald-400"}`}>{pct(m.margin_before_pct)} → {pct(m.margin_after_pct)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <Card className="rounded-2xl border-border bg-card" data-testid="receiving-history-card">
            <CardContent className="p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <Tabs value={sideTab} onValueChange={setSideTab}>
                  <TabsList className="h-8 rounded-lg">
                    <TabsTrigger value="deliveries" className="rounded-md px-2 text-xs" data-testid="receiving-tab-deliveries">Deliveries</TabsTrigger>
                    <TabsTrigger value="trend" className="rounded-md px-2 text-xs" data-testid="receiving-tab-trend">Price trend</TabsTrigger>
                    <TabsTrigger value="changes" className="rounded-md px-2 text-xs" data-testid="receiving-tab-changes">Cost changes</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="flex items-center gap-2">
                  {sideTab === "changes" ? <RangePicker value={range} onChange={(v) => { setRange(v); load(v); }} testIdPrefix="price-changes" /> : null}
                  <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={() => load(range)} disabled={loading} data-testid="price-changes-refresh">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {error ? <ErrorState message={error} onRetry={() => load(range)} testId="price-changes-error" /> : null}
              {loading && deliveries === null ? <Skeleton className="h-32 w-full rounded-2xl" /> : null}

              {sideTab === "deliveries" && deliveries ? (
                deliveries.length ? (
                  <div className="space-y-2" data-testid="deliveries-list">
                    {deliveries.map((d) => (
                      <details key={d.id} className="rounded-xl border border-border p-2" data-testid={`delivery-${d.id}`}>
                        <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 text-xs">
                          <span className="flex items-center gap-1.5 font-medium"><ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />{d.invoice_number ? `#${d.invoice_number}` : "No invoice #"}{d.supplier ? ` · ${d.supplier}` : ""}{d.stock_updated === false ? <Badge variant="outline" className="rounded-md text-[10px]">recorded only</Badge> : null}</span>
                          <span className="text-muted-foreground">{d.lines.length} line{d.lines.length === 1 ? "" : "s"} · <span className="font-semibold text-foreground">{R(d.total_value)}</span> · {d.invoice_date || fmtWhen(d.at)}</span>
                        </summary>
                        <ul className="mt-2 space-y-0.5 border-t border-border pt-2 text-xs">
                          {d.lines.map((l, i) => (
                            <li key={i} className="flex justify-between gap-2"><span>{l.ingredient} · {fmtQty(l.quantity)} {l.unit}{l.location ? <span className="text-muted-foreground"> → {l.location}</span> : null}</span><span className="tabular-nums text-muted-foreground">{l.value !== null && l.value !== undefined ? R(l.value) : "—"}{l.cost_per_unit ? ` (${R(l.cost_per_unit)}/${l.unit})` : ""}</span></li>
                          ))}
                        </ul>
                        <p className="mt-1 text-[11px] text-muted-foreground">Booked by {d.by || "—"} · {fmtWhen(d.at)}</p>
                      </details>
                    ))}
                  </div>
                ) : <EmptyState title="No deliveries yet" body="Every invoice you book appears here with all its lines." testId="deliveries-empty" />
              ) : null}

              {sideTab === "trend" && trend ? (
                trend.items?.length ? (
                  <div className="overflow-x-auto">
                    <p className="mb-2 text-[11px] text-muted-foreground">{trend.summary?.tracked} ingredients tracked · <span className="text-destructive">{trend.summary?.went_up} up</span> · <span className="text-emerald-700 dark:text-emerald-400">{trend.summary?.went_down} down</span> since their previous delivery.</p>
                    <Table>
                      <TableHeader><TableRow><TableHead>Ingredient</TableHead><TableHead className="text-right">Previous → now</TableHead><TableHead className="text-right">Change</TableHead><TableHead className="hidden text-right md:table-cell">Since first</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {trend.items.map((t) => (
                          <TableRow key={t.ingredient_id} data-testid={`price-trend-${t.ingredient_id}`}>
                            <TableCell className="text-xs font-medium">{t.ingredient}<span className="block text-[10px] text-muted-foreground">{t.deliveries} deliver{t.deliveries === 1 ? "y" : "ies"}{t.last_supplier ? ` · ${t.last_supplier}` : ""}</span></TableCell>
                            <TableCell className="whitespace-nowrap text-right text-xs tabular-nums">{t.previous_cost !== null && t.previous_cost !== undefined ? R(t.previous_cost) : "—"} → <span className="font-semibold">{R(t.current_cost)}</span><span className="text-muted-foreground">/{t.unit}</span></TableCell>
                            <TableCell className={`text-right text-xs font-semibold tabular-nums ${t.change_pct > 0 ? "text-destructive" : t.change_pct < 0 ? "text-emerald-700 dark:text-emerald-400" : ""}`}>{t.change_pct === null || t.change_pct === undefined ? "—" : `${t.change_pct > 0 ? "+" : ""}${t.change_pct}%`}</TableCell>
                            <TableCell className={`hidden text-right text-xs tabular-nums md:table-cell ${t.since_first_pct > 0 ? "text-destructive" : t.since_first_pct < 0 ? "text-emerald-700 dark:text-emerald-400" : ""}`}>{t.since_first_pct === null || t.since_first_pct === undefined ? "—" : `${t.since_first_pct > 0 ? "+" : ""}${t.since_first_pct}%`}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : <EmptyState title="No price history yet" body="Book deliveries with their invoice cost and this shows which items are getting more expensive over time." testId="price-trend-empty" />
              ) : null}

              {sideTab === "changes" && changes ? (
                changes.length ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Ingredient</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Change</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {changes.map((c, i) => {
                          const up = Number(c.change_pct) > 0;
                          return (
                            <TableRow key={`${c.ingredient_id}-${c.at}-${i}`} data-testid={`price-change-row-${c.ingredient_id}-${i}`}>
                              <TableCell className="text-xs tabular-nums">{fmtWhen(c.at)}</TableCell>
                              <TableCell className="text-xs font-medium">{c.ingredient}<span className="block text-[10px] text-muted-foreground">{c.supplier || ""}{c.invoice_number ? ` · #${c.invoice_number}` : ""}</span></TableCell>
                              <TableCell className="whitespace-nowrap text-right text-xs tabular-nums">{R(c.previous)} → <span className="font-semibold">{R(c.new)}</span><span className="text-muted-foreground">/{c.unit}</span></TableCell>
                              <TableCell className={`text-right text-xs font-semibold tabular-nums ${c.change_pct === null || c.change_pct === undefined ? "" : up ? "text-destructive" : Number(c.change_pct) < 0 ? "text-emerald-700 dark:text-emerald-400" : ""}`}>{c.change_pct === null || c.change_pct === undefined ? "new" : `${up ? "+" : ""}${Number(c.change_pct).toFixed(1)}%`}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : <EmptyState title="No cost changes in this period" body="Receive a delivery with its invoice cost and any change to an ingredient's cost lands here." testId="price-changes-empty" />
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="receive-preset-dialog">
          <DialogHeader><DialogTitle>Save these lines as a preset</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Next time this supplier delivers, load the preset and only adjust the quantities. The current landed costs are stored so you can spot price increases.</p>
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="receive-preset-name">Preset name</Label>
            <Input id="receive-preset-name" value={presetName} onChange={(e) => setPresetName(e.target.value)} className="h-10 rounded-xl" placeholder="e.g. Weekly bakery order" data-testid="receive-preset-name" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setSavePresetOpen(false)}>Cancel</Button>
            <Button type="button" className="rounded-xl" onClick={savePreset} data-testid="receive-preset-save">Save preset ({filled.length} lines)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
